// Where audio items go between songs.
//
// Rules (agreed with John, Oct 2026):
// - Always at least 2 songs back to back: an audio item only ever comes after
//   2+ songs, and never two audio items in a row.
// - Commercials (the gym's ads) follow the gym's Light / Normal / Busy setting.
// - Everything else fits in around them, roughly at these spacings, in priority order:
//     subscriber: commercial > GymRadio.com jingle > motivational
//     free trial: commercial > free-trial message > jingle > motivational
//   (Class announcements are separate: they come from the gym's timetable and
//   play at a set time before each class, not on a spacing.)
//   An item that's due waits for the next gap if something more important takes it,
//   but one that has waited twice its spacing goes next, so nothing is shut out.
// - After every 2 songs there is ALWAYS an audio item (John, 28 Oct 2026). If
//   nothing is due, the gap is filled: free-trial accounts get a free-trial
//   message, subscribers a GymRadio.com jingle (or a motivational quote).

export const MIN_SONGS_BETWEEN = 2
export const STARVED_AFTER = 2 // x its spacing

// target spacing, in songs, for each kind of audio item
export const COMMERCIAL_EVERY = { light: 7, normal: 4, busy: 2 }
export const FREE_TRIAL_EVERY = 4
export const JINGLE_EVERY = 6
export const CLASS_EVERY = 8
export const MOTIVATION_EVERY = 10
export const TOPSONG_EVERY = 4 // one top song in every 4 music tracks

// Per-channel music rules (John, 2 Nov 2026). Channels not listed use the
// defaults above.
// Lite (reception, lockers):
//   - a Lite top song every 3 songs; a top song can come back after 2 hours
//   - a 90s hit every 6 songs (about 2-3 an hour), never two in a row
//   - everything else from the main Lite stock, no repeat within 3 hours
//   - talking rules are the same as the gym channels (John, 2 Nov 2026)
export const CHANNEL_RULES = {
  lite: {
    topSongEvery: 3,
    topSongRecentHours: 2,
    decadeTags: ['lite90s'],
    decadeEvery: 6,
    decadeOffset: 4
  },
  // Spa, wet areas (John, 2 Nov 2026): music only. No ads, jingles, quotes,
  // free-trial messages or class announcements. Spa tracks are long (cut
  // into ~20-minute parts) and many share the name "Relaxing Spa Music", so
  // repeats are judged by file, not by name, and only the next 2 tracks are
  // saved on the device.
  spa: {
    noTalk: true,
    matchByName: false,
    cacheAhead: 2
  }
}

// The rules for a channel, from its tags (e.g. ['lite'] or ['lite,clean'])
export function channelRules (tags = []) {
  const flat = [].concat(...(tags || []).map((t) => [].concat(t))).map((t) => String(t).split(',')[0])
  const key = Object.keys(CHANNEL_RULES).find((k) => flat.indexOf(k) >= 0)
  return key ? CHANNEL_RULES[key] : {}
}

// Returns an array of slot types: 'topsong' | 'song' | 'decade' (e.g. Lite 90s) |
// 'message' (commercial) | 'freetrial' | 'promotion' (jingle) | 'class' | 'motivation'.
export function buildSchedule ({
  frequency = 'normal',
  commercials = false,
  freeTrial = false,
  jingles = false,
  classes = false,
  motivation = false,
  topSongs = false,
  songs = 120,
  fillWith = null, // 'freetrial' | 'promotion' | 'motivation': plays when nothing else is due
  topSongEvery = TOPSONG_EVERY,
  decadeEvery = 0, // 0 = no decade slot
  decadeOffset = 0,
  minSongsBetween = MIN_SONGS_BETWEEN
} = {}) {
  const rules = [
    { type: 'message', on: commercials, every: COMMERCIAL_EVERY[frequency] || COMMERCIAL_EVERY.normal },
    { type: 'freetrial', on: freeTrial, every: FREE_TRIAL_EVERY },
    { type: 'promotion', on: jingles, every: JINGLE_EVERY },
    { type: 'class', on: classes, every: CLASS_EVERY },
    { type: 'motivation', on: motivation, every: MOTIVATION_EVERY }
  ].filter((r) => r.on)

  // start part-way through each spacing so they don't all land at once
  const since = {}
  rules.forEach((r, i) => { since[r.type] = Math.floor(r.every / 2) - i })

  const out = []
  let songsInARow = 0
  for (let n = 0; n < songs; n++) {
    const isTop = topSongs && n % topSongEvery === 0
    const isDecade = !isTop && decadeEvery > 0 && n % decadeEvery === decadeOffset
    out.push(isTop ? 'topsong' : isDecade ? 'decade' : 'song')
    songsInARow++
    rules.forEach((r) => { since[r.type]++ })
    if (songsInARow < minSongsBetween) continue
    // The most important item that is due gets this gap; the rest wait.
    // So nothing is shut out for good, anything that has waited twice its
    // spacing goes first (once), then priority order applies again.
    const starved = rules.filter((r) => since[r.type] >= r.every * STARVED_AFTER)
      .sort((x, y) => since[y.type] / y.every - since[x.type] / x.every)[0]
    const due = starved || rules.find((r) => since[r.type] >= r.every) ||
      (fillWith ? rules.find((r) => r.type === fillWith) || { type: fillWith } : null)
    if (due) {
      out.push(due.type)
      since[due.type] = 0
      songsInARow = 0
    }
  }
  return out
}
